import { HttpParams } from '@angular/common/http'
import { Component, effect, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { BaseComponent, FeatureType, Grid, ListItem, ModalBackDrop, ModalSize } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { WorkOrderDetailsComponent } from '../scheduling/work-order-details/work-order-details.component'
import { WorkOrderHistoryComponent } from './work-order-history.component'
import { WorkOrderHistoryOpenEvent } from './work-order-history.model'
import { WorkOrderManualComponent } from './work-order-manual.component'
import { WorkOrderQueueItem, WorkOrderQueueResponse } from './work-order-queue.model'

@Component({
    templateUrl: './work-order-queue.component.html',
    styleUrls: ['./work-order-queue.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        WjInputModule,
        WjGridModule,
        WjGridFilterModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        AutoSizeDirective,
        WorkOrderHistoryComponent,
    ],
})
export class WorkOrderQueueComponent extends BaseComponent {
    grid: Grid<WorkOrderQueueRow>
    mechanicsList: ListItem<number>[] = []
    selectedMechanic = signal(-1)
    mineOnly = signal(false)
    historyEquipment = signal('')
    canUseMineOnly = false
    equipmentOptions: ListItem<string>[] = [new ListItem('', 'Select equipment...')]
    private mechanicsLoaded = false

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.EquipmentWorkOrdersEdit])
    }

    constructor() {
        super()

        this.grid = new Grid<WorkOrderQueueRow>(this.services, 'rowKey', this)
            .wrapColumnHeaders()
            .isReadOnly(true)
            .filterBy(item => [
                item.source,
                item.workOrderNumber,
                item.equipment,
                item.equipmentDescription,
                item.job,
                item.description,
                item.systemAffected,
                item.mechanicName,
                item.status,
            ])
            .doInitialSelection(false)

        this.canUseMineOnly = (this.services.authentication.hrref ?? 0) > 0

        effect(() => {
            const mechanicId = this.selectedMechanic()
            const mineOnly = this.mineOnly()
            this.loadQueue(mechanicId, mineOnly)
        })

        this.loadEquipmentOptions()
    }

    loadQueue(mechanicId: number, mineOnly: boolean) {
        let params = new HttpParams().set('hrref', String(mechanicId))
        if (mineOnly)
            params = params.set('mineOnly', 'true')

        this.cancelableRequest(
            'queue',
            true,
            this.services.data.get<WorkOrderQueueResponse>('api/EquipmentWorkOrder/queue', params).subscribe({
                next: response => {
                    this.initialLoadingComplete()
                    if (!this.mechanicsLoaded) {
                        this.mechanicsList = [
                            new ListItem<number>(-1, 'All Mechanics'),
                            new ListItem<number>(0, 'Unassigned'),
                            ...(response.mechanics ?? []).map(m => new ListItem<number>(m.hrRef, m.name)),
                        ]
                        this.mechanicsLoaded = true
                    }
                    this.grid.items = (response.items ?? []).map(item => this.toGridRow(item))
                    this.refreshQueueLayout()
                },
            }),
        )
    }

    onMineOnlyChanged(checked: boolean) {
        this.mineOnly.set(checked)
        if (checked)
            this.selectedMechanic.set(-1)
    }

    openWorkOrderDetails(row: Pick<WorkOrderQueueRow, 'workOrderNumber' | 'equipment' | 'source' | 'webAppsId' | 'job'>) {
        const ref = this.services.modals.open(WorkOrderDetailsComponent, ModalSize.full95Percent, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as WorkOrderDetailsComponent
        instance.workOrder = row.workOrderNumber
        instance.equipmentId = row.equipment
        instance.source = row.source
        instance.workOrderId = row.webAppsId
        instance.job = row.job
    }

    openHistoryWorkOrder(row: WorkOrderHistoryOpenEvent) {
        this.openWorkOrderDetails({
            workOrderNumber: row.workOrderNumber,
            equipment: row.equipment,
            source: row.source,
            webAppsId: row.webAppsId,
            job: row.job ?? '',
        })
    }

    setHistoryEquipment(equipment: string, event?: Event) {
        event?.preventDefault()
        event?.stopPropagation()
        this.historyEquipment.set((equipment ?? '').trim())
        this.refreshQueueLayout()
    }

    onHistoryEquipmentChanged(value: string) {
        this.historyEquipment.set((value ?? '').toString().trim())
        this.refreshQueueLayout()
    }

    private refreshQueueLayout() {
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 0)
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 100)
    }

    private loadEquipmentOptions() {
        this.services.data.get<{ equipment: string; description: string }[]>('api/VpEquipment').subscribe({
            next: rows => {
                const equipment = (rows ?? []).slice().sort((a, b) => (a.equipment ?? '').localeCompare(b.equipment ?? ''))
                this.equipmentOptions = [
                    new ListItem('', 'Select equipment...'),
                    ...equipment.map(x => new ListItem(x.equipment, `${x.equipment}: ${x.description}`)),
                ]
            },
        })
    }

    openManualWorkOrder() {
        const ref = this.services.modals.open(WorkOrderManualComponent, ModalSize.large, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as WorkOrderManualComponent
        instance.parentComponent = this
        ref.result.then(
            () => this.loadQueue(this.selectedMechanic(), this.mineOnly()),
            () => {},
        )
    }

    get canDeleteSelected(): boolean {
        const row = this.grid.selectedItem as WorkOrderQueueRow | null
        return !!row && row.source === 'WebApps' && !!row.webAppsId
    }

    deleteSelectedWorkOrder() {
        const row = this.grid.selectedItem as WorkOrderQueueRow | null
        if (!row || row.source !== 'WebApps' || !row.webAppsId)
            return

        let message = `Permanently delete work order ${row.workOrderNumber}?`
        if (row.linkedIssueReportId)
            message += ' The linked equipment issue report will be kept.'
        else
            message += ' This cannot be undone.'

        this.services.modals.confirm('Delete Work Order', message).then(() => {
            this.services.data.http.delete(`api/EquipmentWorkOrder/${row.webAppsId}`).subscribe({
                next: () => {
                    this.services.notification.success('Work order deleted.')
                    this.loadQueue(this.selectedMechanic(), this.mineOnly())
                },
                error: err => {
                    const body = err?.error
                    const detail = typeof body === 'string' ? body : body?.title ?? body?.message
                    this.services.notification.error(detail ?? 'Failed to delete work order.')
                },
            })
        })
    }

    private toGridRow(item: WorkOrderQueueItem): WorkOrderQueueRow {
        return {
            rowKey: `${item.source}|${item.webAppsId ?? item.workOrderNumber}`,
            source: item.source,
            webAppsId: item.webAppsId,
            workOrderNumber: item.workOrderNumber,
            equipment: item.equipment,
            equipmentDescription: item.equipmentDescription,
            job: item.job,
            description: item.description,
            systemAffected: item.systemAffected,
            jobPriority: item.jobPriority,
            equipmentPriority: item.equipmentPriority,
            queuePosition: item.queuePosition,
            mechanicHrRef: item.mechanicHrRef,
            mechanicName: item.mechanicName ?? '',
            status: item.status,
            dateCreated: item.dateCreated ? new Date(item.dateCreated) : undefined,
            dateSched: item.dateSched ? new Date(item.dateSched) : undefined,
            dateDue: item.dateDue ? new Date(item.dateDue) : undefined,
            isReadOnly: item.isReadOnly,
            linkedIssueReportId: item.linkedIssueReportId,
        }
    }
}

interface WorkOrderQueueRow {
    rowKey: string
    source: string
    webAppsId?: string
    workOrderNumber: string
    equipment: string
    equipmentDescription: string
    job: string
    description: string
    systemAffected: string
    jobPriority: number
    equipmentPriority: number
    queuePosition?: number
    mechanicHrRef?: number
    mechanicName: string
    status: string
    dateCreated?: Date
    dateSched?: Date
    dateDue?: Date
    isReadOnly: boolean
    linkedIssueReportId?: string
}
